import { Box, ChakraProvider, DarkMode, Flex } from '@chakra-ui/react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v6';
import React, { useContext, useEffect } from 'react';
import {
    createBrowserRouter,
    createRoutesFromElements,
    Link,
    Navigate,
    Outlet,
    Route,
    RouterProvider,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import styled, { ThemeProvider } from 'styled-components';

import { FeatureFlag, Role } from '@ndvr/auth-api';

import { ClientDirected } from '@/cip/Portfolio/PortfolioTracker/ClientDirected';
import { OffBalance } from '@/cip/Portfolio/PortfolioTracker/OffBalance';
import { SuspendedPortfolios } from '@/cip/Portfolio/PortfolioTracker/SuspendedPortfolios';
import { UnlinkedFundedAccounts } from '@/cip/Portfolio/PortfolioTracker/UnlinkedFundedAccounts';
import { PortfolioWatcher } from '@/cip/Portfolio/PortfolioWatcher/PortfolioWatcher';
import { AppSidebar } from '@/components/AppSidebar';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { UniversalSearchHotkeys } from '@/components/UniversalSearchHotkeys';
import { Dashboard2Page } from '@/views/dashboard2/Dashboard2Page';
import { Navigation } from 'modules/Navigation';
import { ThemeContext, ThemeController } from 'styles';
import { personListRoute, useRequiredParams, useSearchParamsString } from 'utils';
import { AccountAllocationsTab } from 'views/account/AccountAllocationsTab';
import { AccountLotsTab } from 'views/account/AccountLotsTab';
import { AccountPositionsTab } from 'views/account/AccountPositionsTab';
import { AccountPageRouter } from 'views/account/new-account/AccountPageRouter';
import { AccountTabRedirect } from 'views/account/new-account/AccountTabRedirect';
import { AccountTradesRoute } from 'views/account/new-account/AccountTradesRoute';
import { AccountHoldingsTab } from 'views/account/new-account/holdings/AccountHoldingsTab';
import { AccountInvestmentPolicyTab } from 'views/account/new-account/investment-policy/AccountInvestmentPolicyTab';
import { AccountOverviewRoute } from 'views/account/new-account/overview/AccountOverviewRoute';
import { AccountTransactionsRoute } from 'views/account/new-account/transactions/AccountTransactionsRoute';
import { useUserHasRole } from 'views/admin/dev-queries';
import { Preferences } from 'views/admin/Preferences';
import { PreferencesOverview } from 'views/admin/PreferencesOverview';
import { SystemAdmin } from 'views/admin/SystemAdmin';
import { AuthProvider, RequireAuth } from 'views/auth/Auth';
import { useAssignedFlags } from 'views/auth/auth-queries';
import { Authorize } from 'views/auth/Authorize';
import { LoginWithToken } from 'views/auth/LoginWithToken';
import { Oauth } from 'views/auth/Oauth';
import { OAuthLogin } from 'views/auth/OAuthLogin';
import { BillingGroupsPage } from 'views/billing/billing-groups/BillingGroupsPage';
import { BillingRunsPage } from 'views/billing/billing-runs/BillingRunsPage';
import { FeeSchedulesPage } from 'views/billing/fee-schedules/FeeSchedulesPage';
import { CalendarPage } from 'views/calendar/CalendarPage';
import { BillingHistoryTab } from 'views/household/BillingHistoryTab/BillingHistoryTab';
import { FeeDetailsTab } from 'views/household/BillingHistoryTab/FeeDetailsTab';
import { FilesTab } from 'views/household/FilesTab/FilesTab';
import { HouseholdAccountsTab } from 'views/household/HouseholdAccountsTab';
import { HouseholdBillingGroupsTab } from 'views/household/HouseholdBillingGroupsTab';
import { BillingTab } from 'views/household/HouseholdBillingTab';
import { HouseholdCallPage } from 'views/household/HouseholdCalls/HouseholdCallPage';
import { ClientsTab } from 'views/household/HouseholdClientsTab/HouseholdClientsTab';
import { DetailsTab } from 'views/household/HouseholdDetailsTab';
import { HouseholdEmailPage } from 'views/household/HouseholdEmails/HouseholdEmailPage';
import { HouseholdEventsTab } from 'views/household/HouseholdEvents/HouseholdEventsTab';
import { HouseholdIndexRedirect } from 'views/household/HouseholdIndexRedirect';
import { HouseholdIndividualPage } from 'views/household/HouseholdIndividualPage';
import { HouseholdListPage } from 'views/household/HouseholdListPage';
import { HouseholdMembersTab } from 'views/household/HouseholdMembersTab/HouseholdMembersTab';
import { HouseholdMessagesTab } from 'views/household/HouseholdMessages/HouseholdMessagesTab';
import { EditHouseholdNotePage } from 'views/household/HouseholdNotes/EditHouseholdNotePage';
import { HouseholdNotePage } from 'views/household/HouseholdNotes/HouseholdNotePage';
import { HouseholdNotesTab } from 'views/household/HouseholdNotes/HouseholdNotesTab';
import { MeetingNotePage } from 'views/household/HouseholdNotes/MeetingNotePage';
import { OnboardingTab } from 'views/household/HouseholdOnboardingTab/HouseholdOnboardingTab';
import { HouseholdOpportunitiesTab } from 'views/household/HouseholdOpportunities/HouseholdOpportunitiesTab';
import { HouseholdTasksTab } from 'views/household/HouseholdTasks/HouseholdTasksTab';
import { HouseholdTextsConversation } from 'views/household/HouseholdTexts/HouseholdTextsConversation';
import { HouseholdOverviewTab } from 'views/household/OverviewTab/HouseholdOverviewTab';
import { HouseholdReportingOverviewTab } from 'views/household/ReportingOverviewTab/HouseholdReportingOverviewTab';
import { WashSaleGroups } from 'views/household/WashSaleGroups/WashSaleGroups';
import { MarketingPage, PreferencesCmsRedirect } from 'views/marketing/MarketingPage';
import { OnboardingListPage } from 'views/onboarding/OnboardingListPage';
import { OpportunitiesPage } from 'views/opportunities/OpportunitiesPage';
import { Person } from 'views/person/PersonIndividualPage';
import { AllAccountsPage } from 'views/portfolio-engine/all-accounts/AllAccountsPage';
import { NewModelPage } from 'views/portfolio-engine/allocation-builder/allocations/new-model/NewModelPage';
import { ModelLibraryPage } from 'views/portfolio-engine/allocation-builder/ModelLibraryPage';
import { NewStrategyPage } from 'views/portfolio-engine/allocation-builder/strategies/NewStrategyPage';
import { FundAssetAllocationsView } from 'views/portfolio-engine/fund-asset-allocations/FundAssetAllocationsView';
import { CreateInvestmentRequestDrawer } from 'views/portfolio-engine/investment-request/CreateInvestmentRequestDrawer';
import { InvestmentRequestListPage } from 'views/portfolio-engine/investment-request/InvestmentRequestListPage';
import { ProcessInvestmentRequestDrawer } from 'views/portfolio-engine/investment-request/ProcessInvestmentRequestDrawer';
import { RmdTrackerView } from 'views/portfolio-engine/rmd/RmdTrackerView';
import { ProcessWithdrawalDrawer } from 'views/portfolio-engine/withdrawal/ProcessWithdrawalDrawer';
import { WithdrawalListPage } from 'views/portfolio-engine/withdrawal/WithdrawalListPage';
import { useAccountGroupProfileWithAumIncluded } from 'views/portfolio/account-aum-queries';
import { useAccountGroupGUIDForAccount } from 'views/portfolio/account-queries';
import { PortfolioPage } from 'views/portfolio/PortfolioPage/PortfolioPage';
import {
    AssetLocationTab,
    EquityPreferencesTab,
    LiquidityPlanningTab,
    OverviewTab,
    TargetAllocationTab,
    TaxProfileTab,
} from 'views/portfolio/PortfolioPage/tabs';
import { HouseholdReportingSettingsPage } from 'views/reporting-settings/HouseholdReportingSettingsPage';
import { ReportsPage } from 'views/reports/Reports';
import { TasksPage } from 'views/tasks/TasksPage';
import { Title, ToastContainer, VerticalLayout } from 'widgets';

import { AllocationList } from './cip/Allocation/AllocationList';
import { AssetList } from './cip/Asset/AssetList';
import { Dashboard } from './cip/Dashboard/Dashboard';
import { LotList } from './cip/Lot/LotList';
import { Nav } from './cip/Nav';
import { OptimizationBuilder } from './cip/Optimizer/OptimizationBuilder/OptimizationBuilder';
import { OptimizationList } from './cip/Optimizer/OptimizationList';
import { OptimizationResults } from './cip/Optimizer/OptimizationResults/OptimizationResults';
import { PortfolioList } from './cip/Portfolio/PortfolioList';
import { PositionList } from './cip/Position/PositionList';
import { PricingList } from './cip/Pricing/PricingList';
import { RestrictionsList } from './cip/Restrictions/RestrictionsList';
import { Swagger } from './cip/Swagger';
import { theme } from './cip/theme';
import { TradeBlotter } from './cip/TradeBlotter/TradeBlotter';
import { TradeBlotterContext } from './cip/TradeBlotter/TradeBlotterContext';
import { TradeHistoryList } from './cip/TradeHistory/TradeHistoryList';
import { TransactionsList } from './cip/Transactions/TransactionsList';
import { WithdrawalList } from './cip/Withdrawal/WithdrawalList';
import { ErrorBoundary } from './components/ErrorBoundary';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { Dock } from './views/dock/Dock';

export const App = () => {
    return (
        <TradeBlotterContext>
            <AuthProvider>
                <TooltipProvider>
                    <NuqsAdapter>
                        <RouterProvider router={router} />
                    </NuqsAdapter>
                </TooltipProvider>
            </AuthProvider>
            <Toaster richColors />
        </TradeBlotterContext>
    );
};

const CcThemeRoot = () => {
    const themeContext = useContext(ThemeContext);

    useEffect(() => {
        // trigger Chakra light theme
        document.documentElement.dataset.theme = 'light';
        document.documentElement.style['color-scheme' as any] = 'light';
        document.body.className = 'chakra-ui-light';
    });

    return (
        <ThemeController>
            <ThemeProvider theme={themeContext.light}>
                <Root />
            </ThemeProvider>
        </ThemeController>
    );
};

const CipThemeRoot = () => {
    return (
        <ChakraProvider
            theme={theme}
            toastOptions={{
                defaultOptions: { position: 'top-right', variant: 'solid', isClosable: true },
            }}
        >
            <DarkMode>
                <CipLayout />
            </DarkMode>
        </ChakraProvider>
    );
};

const CipLayout = () => {
    return (
        <RequireAuth>
            <Flex w="100vw" h="100vh">
                <Nav />
                <Flex flex={1} overflow="hidden">
                    <Box w="100%" p={4} mx="auto" overflow="auto">
                        <Outlet />
                    </Box>
                </Flex>
            </Flex>
        </RequireAuth>
    );
};

const router = createBrowserRouter(
    createRoutesFromElements(
        <Route path="/">
            <Route element={<CcThemeRoot />}>
                <Route path="login" element={<OAuthLogin />} />
                <Route path="login/:loginToken" element={<LoginWithToken />} />

                <Route path="oauth" element={<Oauth />} />
                <Route path="authorize" element={<Authorize />} />

                <Route element={<AppLayout />}>
                    <Route index element={<Navigate to="home" replace />} />

                    <Route path="home" element={<Dashboard2Page />} />

                    <Route path="account/:accountGUID" element={<AccountRedirect />} />
                    <Route path="account-group/:accountGroupGUID" element={<AccountGroupRedirect />} />

                    {(['household', 'prospect', 'onboarding'] as const).map((householdType) => {
                        const isHousehold = householdType !== 'prospect';
                        return (
                            <Route path={householdType} key={householdType}>
                                <Route
                                    index
                                    element={
                                        householdType === 'onboarding' ? <OnboardingListPage /> : <HouseholdListPage />
                                    }
                                />

                                <Route path=":globalAccountGUID">
                                    <Route index element={<HouseholdIndexRedirect />} />

                                    <Route path="portfolio/:accountGroupGUID" element={<PortfolioPage />}>
                                        <Route index element={<Navigate to="overview" replace />} />
                                        <Route path="overview" element={<OverviewTab />} />
                                        <Route path="target-allocation" element={<TargetAllocationTab />} />
                                        <Route path="liquidity-planning" element={<LiquidityPlanningTab />} />
                                        <Route path="tax-profile" element={<TaxProfileTab />} />
                                        <Route path="equity-preferences" element={<EquityPreferencesTab />}>
                                            <Route index element={<Navigate to="client" replace />} />
                                            <Route path="client" element={<EquityPreferencesTab />} />
                                            <Route path="sri" element={<EquityPreferencesTab />} />
                                            <Route path="investment-styles" element={<EquityPreferencesTab />} />
                                        </Route>
                                        <Route path="asset-location" element={<AssetLocationTab />} />
                                    </Route>
                                    <Route
                                        path="portfolio/:accountGroupGUID/account/:accountGUID"
                                        element={<AccountPageRouter />}
                                    >
                                        <Route index element={<AccountTabRedirect to="account-overview" />} />
                                        <Route path="account-overview" element={<AccountOverviewRoute />} />
                                        <Route path="investment-policy" element={<AccountInvestmentPolicyTab />} />
                                        <Route path="allocations" element={<AccountAllocationsTab />} />
                                        <Route path="holdings" element={<AccountHoldingsTab />}>
                                            <Route index element={<AccountTabRedirect to="positions" />} />
                                            <Route path="positions" element={<AccountPositionsTab layout="new" />} />
                                            <Route path="lots/:lotType" element={<AccountLotsTab layout="new" />} />
                                        </Route>
                                        <Route path="positions" element={<AccountPositionsTab />} />
                                        <Route path="lots">
                                            <Route index element={<AccountTabRedirect to="open" />} />
                                            <Route path=":lotType" element={<AccountLotsTab />} />
                                        </Route>
                                        <Route path="trades" element={<AccountTradesRoute />} />
                                        <Route path="transactions">
                                            <Route index element={<AccountTabRedirect to="all" />} />
                                            <Route path=":transactionType" element={<AccountTransactionsRoute />} />
                                        </Route>
                                    </Route>

                                    <Route element={<HouseholdIndividualPage />}>
                                        <Route path="reporting" element={<HouseholdReportingOverviewTab />} />
                                        {isHousehold && (
                                            <Route
                                                path="reporting/settings"
                                                element={<HouseholdReportingSettingsPage />}
                                            />
                                        )}
                                        <Route path="accounts" element={<HouseholdAccountsTab />} />
                                        <Route path="portfolio" element={<HouseholdOverviewTab />} />
                                        <Route path="owners" element={<ClientsTab />} />
                                        <Route path="members" element={<HouseholdMembersTab />} />
                                        <Route path="details" element={<DetailsTab />} />
                                        <Route path="notes" element={<HouseholdNotesTab />} />
                                        <Route path="notes/edit" element={<EditHouseholdNotePage />} />
                                        <Route path="notes/view" element={<HouseholdNotePage />} />
                                        <Route path="messages" element={<HouseholdMessagesTab />} />
                                        <Route path="emails/:emailGUID" element={<HouseholdEmailPage />} />
                                        <Route path="texts/thread" element={<HouseholdTextsConversation />} />
                                        <Route path="calls/:zoomPhoneCallGUID" element={<HouseholdCallPage />} />
                                        <Route path="tasks" element={<HouseholdTasksTab />} />
                                        <Route path="events" element={<HouseholdEventsTab />} />
                                        <Route path="opportunities" element={<HouseholdOpportunitiesTab />} />
                                        {isHousehold && (
                                            <Route path="billing-groups" element={<HouseholdBillingGroupsTab />} />
                                        )}
                                        {isHousehold && <Route path="billing-setup" element={<BillingTab />} />}
                                        {isHousehold && (
                                            <Route path="billing-history">
                                                <Route index element={<BillingHistoryTab />} />
                                                <Route path="fee/:feeEntryGUID" element={<FeeDetailsTab />} />
                                            </Route>
                                        )}
                                        {isHousehold && <Route path="tools" element={<WashSaleGroups />} />}
                                        {isHousehold && <Route path="onboarding" element={<OnboardingTab />} />}
                                        <Route path="files" element={<FilesTab />} />
                                    </Route>
                                </Route>
                            </Route>
                        );
                    })}
                    <Route path="person">
                        <Route index element={<Navigate to={personListRoute} replace />} />

                        <Route path=":personOrgGUID">
                            <Route index element={<Navigate to="portfolios" replace />} />

                            <Route path=":tab" element={<Person />} />
                        </Route>
                    </Route>

                    <Route path="notes/view" element={<MeetingNotePage />} />
                    <Route path="tasks" element={<TasksPage />} />
                    <Route path="tasks/:taskGUID" element={<TasksPage />} />
                    <Route path="opportunities" element={<OpportunitiesPage />} />
                    <Route path="opportunities/:opportunityGUID" element={<OpportunitiesPage />} />
                    <Route path="marketing/*" element={<MarketingPage />} />

                    <Route path="dev/*" element={<DevToAdminRedirect />} />
                    <Route
                        path="admin"
                        element={<RequireAdminAccess requiredRole={Role.SYSTEM_ADMIN_ACCESS} redirectTo="/home" />}
                    >
                        <Route index element={<Navigate to="reports" replace />} />
                        <Route path="cc-users" element={<Navigate to="/admin/internal-users" replace />} />
                        <Route path="note-templates" element={<Navigate to="/preferences/note-templates" replace />} />
                        <Route
                            path="keyword-segmentation"
                            element={<Navigate to="/preferences/keyword-segmentation" replace />}
                        />
                        <Route
                            path="account-onboarding/*"
                            element={
                                <RequireAdminAccess requiredRole={Role.SENIOR_ADVISOR} redirectTo="/admin/reports">
                                    <SystemAdmin />
                                </RequireAdminAccess>
                            }
                        />
                        <Route
                            path="move-households/*"
                            element={
                                <RequireAdminAccess
                                    requiredFeatureFlag={FeatureFlag.MOVE_HOUSEHOLDS_ACROSS_TENANTS}
                                    redirectTo="/admin/reports"
                                >
                                    <SystemAdmin />
                                </RequireAdminAccess>
                            }
                        />
                        <Route
                            path="flags/*"
                            element={
                                <RequireAdminAccess
                                    requiredRole={Role.FEATURE_FLAG_MANAGER}
                                    redirectTo="/admin/reports"
                                >
                                    <SystemAdmin />
                                </RequireAdminAccess>
                            }
                        />
                        <Route
                            path="platform/*"
                            element={
                                <RequireAdminAccess
                                    requiredRole={Role.PLATFORM_CONTROLS_EDITOR}
                                    redirectTo="/admin/reports"
                                >
                                    <SystemAdmin />
                                </RequireAdminAccess>
                            }
                        />
                        <Route path=":tab/*" element={<SystemAdmin />} />
                    </Route>

                    <Route path="preferences">
                        <Route index element={<PreferencesOverview />} />
                        <Route path="cms/*" element={<PreferencesCmsRedirect />} />
                        <Route path=":tab/*" element={<Preferences />} />
                    </Route>

                    <Route path="reports">
                        <Route index element={<ReportsPage />} />
                    </Route>

                    <Route path="rmd-tracker" element={<RmdTrackerView />} />
                    <Route
                        path="fund-asset-allocations"
                        element={
                            <RequireAdminAccess requiredRole={Role.IOPS_PORTAL_ACCESS} redirectTo="/home">
                                <FundAssetAllocationsView />
                            </RequireAdminAccess>
                        }
                    />

                    <Route path="calendar" element={<CalendarPage />} />

                    <Route path="trading/withdrawals" element={<WithdrawalListPage />}>
                        <Route path=":withdrawalId" element={<ProcessWithdrawalDrawer />} />
                    </Route>
                    <Route path="trading/investment-requests" element={<InvestmentRequestListPage />}>
                        <Route path="create" element={<CreateInvestmentRequestDrawer />} />
                        <Route path=":requestId" element={<ProcessInvestmentRequestDrawer />} />
                    </Route>

                    <Route path="accounts" element={<AllAccountsPage />} />

                    <Route path="billing">
                        <Route path="billing-runs" element={<BillingRunsPage />} />
                        <Route path="fee-schedules" element={<FeeSchedulesPage />} />
                        <Route path="billing-groups" element={<BillingGroupsPage />} />
                    </Route>

                    <Route path="model-library" element={<ModelLibraryPage />} />
                    <Route path="model-library/strategies/new" element={<NewStrategyPage />} />
                    <Route path="model-library/models/new" element={<NewModelPage />} />

                    <Route path="*" element={<NotFound />} />
                </Route>
            </Route>
            <Route element={<CipThemeRoot />}>
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="security-master" element={<AssetList />} />
                <Route path="pricing" element={<PricingList />} />
                <Route path="positions" element={<PositionList />} />
                <Route path="lots" element={<LotList />} />
                <Route path="portfolios" element={<PortfolioList />} />
                <Route path="trade-history" element={<TradeHistoryList />} />
                <Route path="withdrawals" element={<WithdrawalList />} />
                <Route path="transactions" element={<TransactionsList />} />
                <Route path="restrictions" element={<RestrictionsList />} />
                <Route path="allocation" element={<AllocationList />} />
                <Route path="optimizer">
                    <Route index element={<OptimizationList />} />
                    <Route path="builder" element={<OptimizationBuilder />} />
                    <Route path=":optimizationId" element={<OptimizationResults />} />
                </Route>
                <Route path="portfolio-watcher" element={<PortfolioWatcher />} />
                <Route path="suspended-portfolios" element={<SuspendedPortfolios />} />
                <Route path="client-directed" element={<ClientDirected />} />
                <Route path="unlinked-funded" element={<UnlinkedFundedAccounts />} />
                <Route path="trade-blotter" element={<TradeBlotter />} />
                <Route path="off-balance" element={<OffBalance />} />
                <Route path="swagger" element={<Swagger />} />
            </Route>
        </Route>,
    ),
);

function DevToAdminRedirect() {
    const { search, hash } = useLocation();
    const params = useParams();
    const rest = params['*'] || '';
    const to = `/admin${rest ? `/${rest}` : ''}${search}${hash}`;
    return <Navigate to={to} replace />;
}

function RequireAdminAccess({
    children,
    redirectTo,
    requiredRole,
    requiredFeatureFlag,
}: React.PropsWithChildren<{
    redirectTo: string;
    requiredRole?: Role;
    requiredFeatureFlag?: FeatureFlag;
}>) {
    const roleCheck = useUserHasRole(requiredRole ?? Role.SYSTEM_ADMIN_ACCESS);
    const featureFlagsQuery = useAssignedFlags();

    const isPending =
        (requiredRole ? roleCheck?.isPending : false) || (requiredFeatureFlag ? featureFlagsQuery.isPending : false);

    if (isPending) {
        return null;
    }

    const hasRequiredRole = requiredRole ? !!roleCheck?.data : true;
    const hasRequiredFeatureFlag = requiredFeatureFlag ? !!featureFlagsQuery.data?.includes(requiredFeatureFlag) : true;

    if (!hasRequiredRole || !hasRequiredFeatureFlag) {
        return <Navigate to={redirectTo} replace />;
    }

    return children ? <>{children}</> : <Outlet />;
}

function AccountRedirect() {
    const navigate = useNavigate();
    const searchParams = useSearchParamsString();
    const { accountGUID } = useRequiredParams(['accountGUID']);
    const { data: accountGroupGUID, isLoading } = useAccountGroupGUIDForAccount(accountGUID);
    const { data: accountGroupProfile } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);

    useEffect(() => {
        if (accountGroupProfile) {
            navigate(
                `/household/${accountGroupProfile.accountGroup.householdGUID}/portfolio/${accountGroupGUID}/account/${accountGUID}${searchParams}`,
                { replace: true },
            );
        }
    }, [accountGroupProfile, navigate, accountGroupGUID, accountGUID, searchParams]);

    if (!isLoading && !accountGroupGUID) {
        return 'Not found Account=' + accountGUID;
    }

    return null;
}

function AccountGroupRedirect() {
    const navigate = useNavigate();
    const searchParams = useSearchParamsString();
    const { accountGroupGUID } = useRequiredParams(['accountGroupGUID']);
    const { data: accountGroupProfile, isLoading } = useAccountGroupProfileWithAumIncluded(accountGroupGUID);

    useEffect(() => {
        if (accountGroupProfile) {
            navigate(
                `/household/${accountGroupProfile.accountGroup.householdGUID}/portfolio/${accountGroupGUID}${searchParams}`,
            );
        }
    }, [accountGroupProfile, navigate, accountGroupGUID, searchParams]);

    if (!isLoading && !accountGroupProfile) {
        return 'Not found AccountGroup=' + accountGroupGUID;
    }

    return null;
}

function Root() {
    return (
        <ErrorBoundary>
            <Layout>
                <Outlet />
            </Layout>
            <UniversalSearchHotkeys />
            <ToastContainer />
            <Dock />
        </ErrorBoundary>
    );
}

function AppLayout() {
    return (
        <RequireAuth>
            <AppLayoutRoot>
                <ImpersonationBanner />
                <AppLayoutBody>
                    <Navigation />
                    <CenterPane>
                        <ErrorBoundary>
                            <Outlet />
                        </ErrorBoundary>
                    </CenterPane>
                    <AppSidebar />
                </AppLayoutBody>
            </AppLayoutRoot>
        </RequireAuth>
    );
}

const AppLayoutRoot = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    position: absolute;
    overflow: hidden;
`;

const AppLayoutBody = styled.div`
    display: flex;
    flex-direction: row;
    flex: 1;
    min-height: 0;
    overflow: hidden;
`;

const CenterPane = styled.div`
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    overflow-y: auto;
    min-height: 0;
    background: #ffffff;
`;

function NotFound() {
    return (
        <VerticalLayout $stretch $rowCenter $columnCenter>
            <Title page>{`There's nothing here :(`}</Title>
            <Link to="/household">Back to Household list</Link>
        </VerticalLayout>
    );
}

const Layout = styled.main`
    display: flex;
    height: 100%;
    width: 100%;
    position: absolute;
    overflow: hidden;
`;
